/**
 * Workbench — the right-hand pane of the AgentWindow (Devin-style).
 *
 * V1 tabs:
 *   - Progress: active run's plan checklist + step cursor
 *   - Questions: pending + answered ask_user cards
 *   - Context: current scope + model/specialist/notebook settings
 *   - Activity: durable run-event feed across the session
 */

import { useState } from "react";
import {
  ListChecks,
  HelpCircle,
  Layers,
  Activity,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Bot,
  PauseCircle,
} from "lucide-react";
import { cn } from "@flowstate/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { TimelineEvent, TimelineRun } from "../engine/cortexStreamReducer";
import { PlanCard } from "./timeline/PlanCard";
import { QuestionCard } from "./timeline/QuestionCard";

type Tab = "progress" | "questions" | "context" | "activity";

interface WorkbenchProps {
  runs: TimelineRun[];
  orgId?: string;
  projectName?: string;
  modelName?: string;
  specialist?: string;
  notebookId?: string;
  onAnswer: (runId: string, opts: { approved?: boolean; value?: string }) => void;
  className?: string;
}

const TABS: { id: Tab; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: "progress", label: "Progress", icon: ListChecks },
  { id: "questions", label: "Questions", icon: HelpCircle },
  { id: "context", label: "Context", icon: Layers },
  { id: "activity", label: "Activity", icon: Activity },
];

function eventLabel(e: TimelineEvent): { icon: React.ReactNode; text: string } {
  const d = e.data;
  switch (e.kind) {
    case "run_marker": {
      const phase = String(d.phase ?? "");
      const labels: Record<string, string> = {
        started: "Run started",
        finished: "Run finished",
        failed: "Run failed",
        awaiting: "Paused for specialist",
        awaiting_input: "Paused for input",
        resumed: "Resumed",
      };
      return {
        icon:
          phase === "failed" ? (
            <XCircle className="h-3 w-3 text-destructive" />
          ) : phase === "finished" ? (
            <CheckCircle2 className="h-3 w-3 text-green-500" />
          ) : phase === "awaiting" || phase === "awaiting_input" ? (
            <PauseCircle className="h-3 w-3 text-amber-500" />
          ) : (
            <Activity className="h-3 w-3 text-primary" />
          ),
        text: labels[phase] ?? `Run ${phase}`,
      };
    }
    case "status":
      return {
        icon: <Loader2 className="h-3 w-3 text-primary" />,
        text: String(d.message ?? d.status ?? "status"),
      };
    case "plan":
      return {
        icon: <ListChecks className="h-3 w-3 text-primary" />,
        text: `Plan — ${Array.isArray(d.steps) ? d.steps.length : 0} steps`,
      };
    case "tool":
      return {
        icon: <CheckCircle2 className="h-3 w-3 text-muted-foreground" />,
        text: `${d.phase === "start" ? "→" : "←"} ${String(d.tool ?? "")}`,
      };
    case "sub_agent": {
      const agents = (d.agents as Array<Record<string, unknown>>) ?? [];
      const names = agents
        .map((a) => String(a.displayName ?? a.specialistType ?? ""))
        .filter(Boolean)
        .join(", ");
      return {
        icon: <Bot className="h-3 w-3 text-primary" />,
        text: `${d.phase === "dispatch" ? "Dispatched" : "Returned"} ${names}`.trim(),
      };
    }
    case "question":
      return {
        icon: <HelpCircle className="h-3 w-3 text-primary" />,
        text: `Asked: ${String(d.question ?? "").slice(0, 80)}`,
      };
    case "specialist_data":
      return {
        icon: <CheckCircle2 className="h-3 w-3 text-muted-foreground" />,
        text: `Data from ${String(d.tool ?? "specialist")}`,
      };
    case "error":
      return {
        icon: <AlertTriangle className="h-3 w-3 text-destructive" />,
        text: String(d.message ?? "error").slice(0, 100),
      };
    default:
      return { icon: <Activity className="h-3 w-3" />, text: e.kind };
  }
}

const LIFECYCLE_DOT: Record<string, string> = {
  running: "bg-green-500 animate-pulse",
  awaiting: "bg-amber-500",
  awaiting_input: "bg-primary animate-pulse",
  complete: "bg-muted-foreground/40",
  failed: "bg-destructive",
};

export function Workbench({
  runs,
  orgId,
  projectName,
  modelName,
  specialist,
  notebookId,
  onAnswer,
  className,
}: WorkbenchProps) {
  const [tab, setTab] = useState<Tab>("progress");

  const allEvents = runs.flatMap((r) => r.events.map((e) => ({ run: r, event: e })));
  const pendingQuestions = runs.filter((r) => r.lifecycle === "awaiting_input" && r.question);
  const answeredQuestions = runs.filter(
    (r) => r.question && r.lifecycle !== "awaiting_input",
  );
  const latestPlanRun = [...runs].reverse().find((r) => r.plan);
  const activeRun = runs.find(
    (r) => r.lifecycle === "running" || r.lifecycle === "awaiting",
  );

  return (
    <div
      className={cn(
        "flex flex-col h-full min-h-0 border-l border-border/30 bg-background/50",
        className,
      )}
    >
      {/* Tab bar — labels only on the active tab so the bar never overflows
          the (user-resizable) panel width. */}
      <div className="flex items-center gap-0.5 px-2 pt-2 border-b border-border/30 overflow-hidden">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            title={label}
            aria-label={label}
            onClick={() => setTab(id)}
            className={cn(
              "flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-t-md transition-colors min-w-0 shrink-0",
              tab === id
                ? "bg-muted/60 text-foreground border-b-2 border-primary"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/30",
            )}
          >
            <Icon className="h-3.5 w-3.5 shrink-0" />
            {tab === id && <span className="truncate">{label}</span>}
            {id === "questions" && pendingQuestions.length > 0 && (
              <span className="h-4 min-w-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center shrink-0">
                {pendingQuestions.length}
              </span>
            )}
          </button>
        ))}
      </div>

      <ScrollArea className="flex-1 min-h-0 min-w-0">
        <div className="p-3 space-y-3 min-w-0">
          {tab === "progress" && (
            <>
              {activeRun && (
                <div className="text-[11px] text-muted-foreground flex items-center gap-2 min-w-0">
                  <Loader2 className="h-3 w-3 animate-spin shrink-0" />
                  <span className="truncate">
                    Run {activeRun.id.slice(0, 8)} — {activeRun.statusMessage ?? activeRun.lifecycle}
                  </span>
                </div>
              )}
              {latestPlanRun?.plan ? (
                <PlanCard
                  plan={latestPlanRun.plan}
                  currentStep={latestPlanRun.currentStep}
                  isRunning={
                    latestPlanRun.lifecycle === "running" || latestPlanRun.lifecycle === "awaiting"
                  }
                />
              ) : (
                <p className="text-xs text-muted-foreground py-6 text-center">
                  No plan yet — plans appear when Cortex breaks a request into steps.
                </p>
              )}
              {runs
                .filter((r) => r.plan && r !== latestPlanRun)
                .map((r) => (
                  <PlanCard key={r.id} plan={r.plan!} isRunning={false} />
                ))}
            </>
          )}

          {tab === "questions" && (
            <>
              {pendingQuestions.length === 0 && answeredQuestions.length === 0 && (
                <p className="text-xs text-muted-foreground py-6 text-center">
                  No questions — when Cortex needs a decision it'll ask here.
                </p>
              )}
              {pendingQuestions.map((r) => (
                <QuestionCard key={r.id} runId={r.id} question={r.question!} onAnswer={onAnswer} />
              ))}
              {answeredQuestions.map((r) => (
                <QuestionCard
                  key={r.id}
                  runId={r.id}
                  question={r.question!}
                  answered={{ approved: true }}
                  onAnswer={onAnswer}
                  disabled
                />
              ))}
            </>
          )}

          {tab === "context" && (
            <>
              <div>
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground/70 mb-1.5">
                  Scope
                </p>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Organization</span>
                    <span className="text-foreground/80 font-mono text-[10px]">
                      {orgId ? orgId.slice(0, 8) : "—"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Project</span>
                    <span className="text-foreground/80">{projectName ?? "All projects"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Runs</span>
                    <span className="text-foreground/80">{runs.length}</span>
                  </div>
                </div>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground/70 mb-1.5">
                  Configuration
                </p>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Model</span>
                    <span className="text-foreground/80">{modelName ?? "auto"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Specialist</span>
                    <span className="text-foreground/80">{specialist ?? "auto-route"}</span>
                  </div>
                  {notebookId && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Notebook</span>
                      <span className="text-foreground/80 font-mono text-[10px]">
                        {notebookId.slice(0, 8)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {tab === "activity" && (
            <>
              {/* Run lifecycle strip */}
              {runs.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pb-1">
                  {runs.map((r) => (
                    <span
                      key={r.id}
                      className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-muted/40 text-muted-foreground font-mono"
                      title={r.id}
                    >
                      <span className={cn("h-1.5 w-1.5 rounded-full", LIFECYCLE_DOT[r.lifecycle])} />
                      {r.id.slice(0, 6)}
                    </span>
                  ))}
                </div>
              )}
              {allEvents.length === 0 && (
                <p className="text-xs text-muted-foreground py-6 text-center">
                  No activity yet — run events stream here in real time and are
                  persisted for history.
                </p>
              )}
              <ol className="space-y-1">
                {[...allEvents].reverse().map(({ run, event }) => {
                  const { icon, text } = eventLabel(event);
                  return (
                    <li
                      key={`${run.id}-${event.at}-${event.seq ?? 0}-${event.kind}`}
                      className="flex items-center gap-2 text-[11px] text-muted-foreground py-1 px-1.5 rounded hover:bg-muted/30"
                    >
                      <span className="shrink-0">{icon}</span>
                      <span className="flex-1 min-w-0 truncate">{text}</span>
                      <span className="text-[9px] text-muted-foreground/50 font-mono shrink-0">
                        {new Date(event.at).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </li>
                  );
                })}
              </ol>
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
